import { useState, useEffect } from 'react'
import api from '../services/api'

interface Empresa {
  empcod: number
  empnom: string
  empfan: string
}

interface Parametros {
  empresa_codigo: number
  dissidio_codigo: string
  dissidio_descricao: string
  empresa_nome?: string
  altera_horas: boolean
  horas_mes: number
  modelo_impressao: string
  intervalo_intrajornada: string
  qtd_intervalos_intrajornada: number
  tempo_minimo_intervalo_intrajornada: string
  intervalo_interjornada: string
  tempo_minimo_intervalo_interjornada: string
  soma_diferenca_interjornada_intrajornada: boolean
  tempo_maximo_soma_interjornada_intrajornada: string
  tempo_dsr: string
  usa_banco_horas: boolean
  periodos_mensais_banco: number
  regra_pagamento_he: string
  calcula_he_100_domingo_feriado: boolean
  mostra_hr: boolean
  horario_adn_inicio: string
  horario_adn_fim: string
  regra_adn: string
  dia_inicio_periodo: number
}

const HORAS_MES = [220, 180, 120]
const QTD_INTERVALOS = [1, 2, 3, 4]
const PERIODOS_BANCO = [1, 2, 3, 4, 5, 6]
const MODELOS_IMPRESSAO = ['Normal']
const REGRAS_PAGAMENTO_HE = ['50% folha e 50% banco', '100% banco']
const REGRAS_ADN = ['integral', 'horas trabalhadas', 'estendido']

function formVazio(empresaPadrao: number): Parametros {
  return {
    empresa_codigo: empresaPadrao,
    dissidio_codigo: '',
    dissidio_descricao: '',
    altera_horas: false,
    horas_mes: 220,
    modelo_impressao: 'Normal',
    intervalo_intrajornada: '01:00',
    qtd_intervalos_intrajornada: 1,
    tempo_minimo_intervalo_intrajornada: '00:15',
    intervalo_interjornada: '11:00',
    tempo_minimo_intervalo_interjornada: '11:00',
    soma_diferenca_interjornada_intrajornada: false,
    tempo_maximo_soma_interjornada_intrajornada: '00:00',
    tempo_dsr: '35:00',
    usa_banco_horas: false,
    periodos_mensais_banco: 1,
    regra_pagamento_he: '50% folha e 50% banco',
    calcula_he_100_domingo_feriado: false,
    mostra_hr: false,
    horario_adn_inicio: '22:00',
    horario_adn_fim: '05:00',
    regra_adn: 'integral',
    dia_inicio_periodo: 1,
  }
}

function soHoraMinuto(v: string) {
  return (v || '').slice(0, 5)
}

function intervalParaHM(v: string) {
  const [h, m] = (v || '0:00').split(':')
  return `${Number(h) || 0}:${(m || '00').padStart(2, '0')}`
}

function horasDe(hm: string) {
  return Number((hm || '0:0').split(':')[0]) || 0
}

function minutosDe(hm: string) {
  return Number((hm || '0:0').split(':')[1]) || 0
}

function combinaHM(horas: number, minutos: number) {
  return `${horas}:${String(minutos).padStart(2, '0')}`
}

// mesma regra do backend (calcularPeriodoFicha): dia 1 = mês inteiro; qualquer outro dia
// N = período de N do mês anterior a (N-1) do mês corrente — só pra dar uma prévia aqui.
function descricaoPeriodo(dia: number) {
  if (dia <= 1) return 'Do dia 1 ao último dia do mês corrente'
  return `Do dia ${dia} do mês anterior ao dia ${dia - 1} do mês corrente`
}

const SIGLAS: Record<string, string> = {
  DSR: 'Descanso Semanal Remunerado',
  HE: 'Horas Extras',
  HR: 'Hora Reduzida',
  ADN: 'Adicional Noturno',
}

const SIGLAS_REGEX = new RegExp(`\\b(${Object.keys(SIGLAS).join('|')})\\b`, 'g')

function Rotulo({ children }: { children: string }) {
  const partes = children.split(SIGLAS_REGEX)
  return (
    <>
      {partes.map((parte, i) =>
        SIGLAS[parte] ? (
          <abbr key={i} title={SIGLAS[parte]} style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
            {parte}
          </abbr>
        ) : (
          <span key={i}>{parte}</span>
        ),
      )}
    </>
  )
}

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: Parametros[]
  onAdicionar: () => void
  onEditar: (p: Parametros) => void
  onExcluir: (p: Parametros) => void
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
          Parâmetros cadastrados ({lista.length})
        </h3>
        <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
      </div>

      {lista.length === 0 && (
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhum parâmetro cadastrado.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lista.map(p => (
          <div
            key={`${p.empresa_codigo}-${p.dissidio_codigo}`}
            style={{
              border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>
                {p.empresa_codigo} - {p.empresa_nome} · {p.dissidio_codigo} - {p.dissidio_descricao}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>{p.horas_mes}h/mês</span>
                <span>Período: <b>{descricaoPeriodo(p.dia_inicio_periodo)}</b></span>
                <span>Banco de horas: <b>{p.usa_banco_horas ? 'Sim' : 'Não'}</b></span>
                <span><Rotulo>ADN</Rotulo>: <b>{p.horario_adn_inicio}–{p.horario_adn_fim}</b> ({p.regra_adn})</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(p)}>Editar</button>
              <button
                className="btn btn-outline btn-sm"
                style={{ color: '#b91c1c' }}
                onClick={() => onExcluir(p)}
              >
                Excluir
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ empresas, editando, formInicial, onVoltar, onSalvo }: {
  empresas: Empresa[]
  editando: boolean
  formInicial: Parametros
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<Parametros>(formInicial)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (!form.empresa_codigo || !form.dissidio_codigo.trim() || !form.dissidio_descricao.trim()) {
      setErro('Empresa, código e descrição do dissídio são obrigatórios.')
      return
    }
    setSalvando(true)
    setErro('')
    try {
      if (editando) {
        await api.put(`/ficha-ponto/dissidios/${form.dissidio_codigo}`, { descricao: form.dissidio_descricao })
        await api.put(`/ficha-ponto/parametros/${form.empresa_codigo}/${form.dissidio_codigo}`, form)
      } else {
        await api.post('/ficha-ponto/dissidios', { codigo: form.dissidio_codigo, descricao: form.dissidio_descricao })
        await api.post('/ficha-ponto/parametros', form)
      }
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
          {editando ? 'Editar Parâmetros de Ficha Ponto' : 'Novo Grupo de Regras de Ficha Ponto'}
        </h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Empresa *</label>
          <select
            value={form.empresa_codigo}
            disabled={editando}
            onChange={e => setForm(f => ({ ...f, empresa_codigo: Number(e.target.value) }))}
          >
            {empresas.map(emp => (
              <option key={emp.empcod} value={emp.empcod}>{emp.empcod} - {emp.empfan || emp.empnom}</option>
            ))}
          </select>
        </div>

        <div className="field" />

        <div className="field">
          <label>Código do dissídio *</label>
          <input
            type="text"
            value={form.dissidio_codigo}
            disabled={editando}
            onChange={e => setForm(f => ({ ...f, dissidio_codigo: e.target.value.slice(0, 20) }))}
            placeholder="Ex: MOT2024"
          />
        </div>

        <div className="field">
          <label>Descrição do dissídio *</label>
          <input
            type="text"
            value={form.dissidio_descricao}
            onChange={e => setForm(f => ({ ...f, dissidio_descricao: e.target.value.slice(0, 200) }))}
            placeholder="Ex: Dissídio Motoristas 2024"
          />
        </div>

        <div className="field" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'end', gap: 12 }}>
          <div style={{ width: 160 }}>
            <label>Dia de início do período *</label>
            <input
              type="number"
              min={1}
              max={31}
              value={form.dia_inicio_periodo}
              onChange={e => setForm(f => ({ ...f, dia_inicio_periodo: Math.min(31, Math.max(1, Number(e.target.value) || 1)) }))}
            />
          </div>
          <span style={{ fontSize: '0.78rem', color: '#6b7280', paddingBottom: 8 }}>{descricaoPeriodo(form.dia_inicio_periodo)}</span>
        </div>

        <div className="field">
          <label>Altera horas ficha ponto</label>
          <select
            value={form.altera_horas ? '1' : '0'}
            onChange={e => setForm(f => ({ ...f, altera_horas: e.target.value === '1' }))}
          >
            <option value="0">Não</option>
            <option value="1">Sim</option>
          </select>
        </div>

        <div className="field">
          <label>Número de horas / mês</label>
          <select
            value={form.horas_mes}
            onChange={e => setForm(f => ({ ...f, horas_mes: Number(e.target.value) }))}
          >
            {HORAS_MES.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>

        <div className="field">
          <label>Modelo de impressão</label>
          <select
            value={form.modelo_impressao}
            onChange={e => setForm(f => ({ ...f, modelo_impressao: e.target.value }))}
          >
            {MODELOS_IMPRESSAO.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="field" />

        <div className="field">
          <label>Tempo de intervalo intrajornada *</label>
          <input
            type="time"
            value={form.intervalo_intrajornada}
            onChange={e => setForm(f => ({ ...f, intervalo_intrajornada: e.target.value }))}
          />
        </div>

        <div className="field">
          <label>Quantidade de intervalos intrajornada</label>
          <select
            value={form.qtd_intervalos_intrajornada}
            onChange={e => setForm(f => ({ ...f, qtd_intervalos_intrajornada: Number(e.target.value) }))}
          >
            {QTD_INTERVALOS.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>

        <div className="field">
          <label>Tempo mínimo para intervalo intrajornada *</label>
          <input
            type="time"
            value={form.tempo_minimo_intervalo_intrajornada}
            onChange={e => setForm(f => ({ ...f, tempo_minimo_intervalo_intrajornada: e.target.value }))}
          />
        </div>

        <div className="field">
          <label>Tempo de intervalo interjornada *</label>
          <input
            type="time"
            value={form.intervalo_interjornada}
            onChange={e => setForm(f => ({ ...f, intervalo_interjornada: e.target.value }))}
          />
        </div>

        <div className="field">
          <label>Tempo de intervalo mínimo interjornada *</label>
          <input
            type="time"
            value={form.tempo_minimo_intervalo_interjornada}
            onChange={e => setForm(f => ({ ...f, tempo_minimo_intervalo_interjornada: e.target.value }))}
          />
        </div>

        <div className="field">
          <label>Soma diferença interjornada no tempo de intervalo intrajornada</label>
          <select
            value={form.soma_diferenca_interjornada_intrajornada ? '1' : '0'}
            onChange={e => setForm(f => ({ ...f, soma_diferenca_interjornada_intrajornada: e.target.value === '1' }))}
          >
            <option value="0">Não</option>
            <option value="1">Sim</option>
          </select>
        </div>

        <div className="field">
          <label>Limite máximo da soma interjornada→intrajornada</label>
          <input
            type="time"
            disabled={!form.soma_diferenca_interjornada_intrajornada}
            value={form.tempo_maximo_soma_interjornada_intrajornada}
            onChange={e => setForm(f => ({ ...f, tempo_maximo_soma_interjornada_intrajornada: e.target.value }))}
          />
        </div>

        <div className="field">
          <label><Rotulo>Tempo para DSR *</Rotulo></label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="number"
              min={0}
              value={horasDe(form.tempo_dsr)}
              onChange={e => setForm(f => ({ ...f, tempo_dsr: combinaHM(Number(e.target.value) || 0, minutosDe(f.tempo_dsr)) }))}
              style={{ width: 70 }}
            />
            <span>h</span>
            <input
              type="number"
              min={0}
              max={59}
              value={minutosDe(form.tempo_dsr)}
              onChange={e => setForm(f => ({ ...f, tempo_dsr: combinaHM(horasDe(f.tempo_dsr), Number(e.target.value) || 0) }))}
              style={{ width: 60 }}
            />
            <span>min</span>
          </div>
        </div>

        <div className="field" />

        <div className="field">
          <label>Utiliza banco de horas</label>
          <select
            value={form.usa_banco_horas ? '1' : '0'}
            onChange={e => setForm(f => ({ ...f, usa_banco_horas: e.target.value === '1' }))}
          >
            <option value="0">Não</option>
            <option value="1">Sim</option>
          </select>
        </div>

        <div className="field">
          <label>Períodos mensais para banco</label>
          <select
            value={form.periodos_mensais_banco}
            onChange={e => setForm(f => ({ ...f, periodos_mensais_banco: Number(e.target.value) }))}
          >
            {PERIODOS_BANCO.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Regra de pagamento de horas extras</label>
          <select
            value={form.regra_pagamento_he}
            onChange={e => setForm(f => ({ ...f, regra_pagamento_he: e.target.value }))}
          >
            {REGRAS_PAGAMENTO_HE.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div className="field">
          <label><Rotulo>Calcula HE 100% domingo e feriados</Rotulo></label>
          <select
            value={form.calcula_he_100_domingo_feriado ? '1' : '0'}
            onChange={e => setForm(f => ({ ...f, calcula_he_100_domingo_feriado: e.target.value === '1' }))}
          >
            <option value="0">Não</option>
            <option value="1">Sim</option>
          </select>
        </div>

        <div className="field">
          <label><Rotulo>Mostra HR</Rotulo></label>
          <select
            value={form.mostra_hr ? '1' : '0'}
            onChange={e => setForm(f => ({ ...f, mostra_hr: e.target.value === '1' }))}
          >
            <option value="0">Não</option>
            <option value="1">Sim</option>
          </select>
        </div>

        <div className="field">
          <label><Rotulo>Horário ADN — início *</Rotulo></label>
          <input
            type="time"
            value={form.horario_adn_inicio}
            onChange={e => setForm(f => ({ ...f, horario_adn_inicio: e.target.value }))}
          />
        </div>

        <div className="field">
          <label><Rotulo>Horário ADN — fim *</Rotulo></label>
          <input
            type="time"
            value={form.horario_adn_fim}
            onChange={e => setForm(f => ({ ...f, horario_adn_fim: e.target.value }))}
          />
        </div>

        <div className="field">
          <label><Rotulo>Regra ADN</Rotulo></label>
          <select
            value={form.regra_adn}
            onChange={e => setForm(f => ({ ...f, regra_adn: e.target.value }))}
          >
            {REGRAS_ADN.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>
    </div>
  )
}

export default function ParametrosFichaPonto() {
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [lista, setLista] = useState<Parametros[]>([])
  const [carregado, setCarregado] = useState(false)
  const [modo, setModo] = useState<'lista' | 'form'>('lista')
  const [formAtual, setFormAtual] = useState<Parametros | null>(null)
  const [editando, setEditando] = useState(false)

  async function carregar() {
    const [rEmp, rParam] = await Promise.all([
      api.get('/erp/empresas'),
      api.get('/ficha-ponto/parametros'),
    ])
    setEmpresas(rEmp.data)
    setLista(rParam.data.map((p: Parametros) => ({
      ...p,
      intervalo_intrajornada: soHoraMinuto(p.intervalo_intrajornada),
      tempo_minimo_intervalo_intrajornada: soHoraMinuto(p.tempo_minimo_intervalo_intrajornada),
      intervalo_interjornada: soHoraMinuto(p.intervalo_interjornada),
      tempo_minimo_intervalo_interjornada: soHoraMinuto(p.tempo_minimo_intervalo_interjornada),
      tempo_maximo_soma_interjornada_intrajornada: soHoraMinuto(p.tempo_maximo_soma_interjornada_intrajornada),
      tempo_dsr: intervalParaHM(p.tempo_dsr),
      horario_adn_inicio: soHoraMinuto(p.horario_adn_inicio),
      horario_adn_fim: soHoraMinuto(p.horario_adn_fim),
    })))
    setCarregado(true)
  }

  useEffect(() => { carregar() }, [])

  function adicionar() {
    setFormAtual(formVazio(empresas[0]?.empcod ?? 0))
    setEditando(false)
    setModo('form')
  }

  function editar(p: Parametros) {
    setFormAtual({ ...p })
    setEditando(true)
    setModo('form')
  }

  async function excluir(p: Parametros) {
    if (!window.confirm(`Excluir parâmetros da empresa ${p.empresa_codigo} / dissídio ${p.dissidio_codigo}?`)) return
    await api.delete(`/ficha-ponto/parametros/${p.empresa_codigo}/${p.dissidio_codigo}`)
    await carregar()
  }

  function voltar() {
    setModo('lista')
    setFormAtual(null)
  }

  async function salvo() {
    await carregar()
    setModo('lista')
    setFormAtual(null)
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>Parâmetros de Ficha Ponto</h2>

      {!carregado ? null : modo === 'lista' ? (
        <Lista lista={lista} onAdicionar={adicionar} onEditar={editar} onExcluir={excluir} />
      ) : formAtual ? (
        <Formulario
          empresas={empresas}
          editando={editando}
          formInicial={formAtual}
          onVoltar={voltar}
          onSalvo={salvo}
        />
      ) : null}
    </div>
  )
}
